import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_TRANSACTION_PRODUCTS } from 'api/graphql/queries/transactions/getTransactionDetails';
import { OrderTotals } from 'components/transactions/types';

// Product item interface
export interface ProductItem {
  id: string;
  order_id: string;
  order_number: string;
  product_name: string;
  sku: string;
  price: number;
  qty: number;
  total_price: number;
  total_tax: number;
  coupon_amount: number;
  shipping_amount: number;
  commission: number;
  subtotal: number;
  image: string;
}

interface TransactionProductsResult {
  products: ProductItem[];
  subtotal: number;
  loading: boolean;
  error: any;
  transactionId?: string;
  orderTotals?: OrderTotals;
}

/**
 * Custom hook to fetch product details related to a specific transaction
 * 
 * @param entityTransactionId - The transaction ID to fetch product details for
 * @returns Object containing product data, loading state, and any errors
 */
export function useTransactionProductsData(entityTransactionId: string): TransactionProductsResult {
  // State for products data
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [subtotal, setSubtotal] = useState<number>(0);
  const [transactionId, setTransactionId] = useState<string | undefined>(undefined);
  const [orderTotals, setOrderTotals] = useState<OrderTotals | undefined>(undefined);
  
  // Query for fetching product details by transaction ID
  const [fetchProducts, { loading, error, data }] = useLazyQuery(
    GET_TRANSACTION_PRODUCTS, 
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );
  
  // Transform API response to match the ProductItem interface
  const transformProducts = useCallback((apiItems: any[]): ProductItem[] => {
    return apiItems.map((item, index) => ({
      id: `${item.order_id}-${index}`,
      order_id: item.order_id || '',
      order_number: item.order_number || '',
      product_name: item.product_name || '',
      sku: item.sku || '',
      price: item.price || 0,
      qty: item.qty || 0,
      total_price: item.total_price || 0,
      total_tax: item.total_tax || 0,
      coupon_amount: item.coupon_amount || 0,
      shipping_amount: item.shipping_amount || 0,
      commission: item.commission || 0,
      subtotal: item.subtotal || 0,
      image: item.image || ''
    }));
  }, []);
  
  // Fetch products when entityTransactionId changes
  useEffect(() => {
    if (entityTransactionId) {
      // Check if valid ID (can be parsed as number) and fetch products
      const parsedId = parseInt(entityTransactionId, 10);
      const validId = !isNaN(parsedId) ? parsedId : undefined;
      
      if (validId) {
        fetchProducts({
          variables: {
            transactionId: validId
          }
        });
      }
    }
  }, [entityTransactionId, fetchProducts]);
  
  // Update products state when API response is received
  useEffect(() => {
    if (data?.orderDetailsByTransactionId) {
      if (data.orderDetailsByTransactionId.items) {
        const transformedProducts = transformProducts(data.orderDetailsByTransactionId.items);
        setProducts(transformedProducts);
        
        // Define a type for the API items
        type ApiItem = {
          total_price?: number;
          commission?: number;
          [key: string]: any;
        };

        // Always calculate totals directly from the items array
        const total_amount = data.orderDetailsByTransactionId.items.reduce(
          (sum: number, item: ApiItem) => sum + (item.total_price || 0), 
          0
        );
        
        const total_commission = data.orderDetailsByTransactionId.items.reduce(
          (sum: number, item: ApiItem) => sum + (item.commission || 0), 
          0
        );
        
        const total_transaction = total_amount - total_commission;
        
        // Create order totals object
        const calculatedTotals = {
          total_amount,
          total_commission,
          total_transaction
        };
        
        console.log("Calculated totals from items:", calculatedTotals);
        setOrderTotals(calculatedTotals);
      } else {
        setProducts([]);
      }
      
      // Set subtotal
      setSubtotal(data.orderDetailsByTransactionId.subtotal || 0);
      
      // Store the transaction_id from the response if available
      if (data.orderDetailsByTransactionId.transaction_id) {
        setTransactionId(String(data.orderDetailsByTransactionId.transaction_id));
      }
    } else {
      setProducts([]);
      setSubtotal(0);
      setOrderTotals(undefined);
    }
  }, [data, transformProducts]);
  
  return {
    products,
    subtotal,
    loading,
    error,
    transactionId,
    orderTotals
  };
}
